/**
 * Cookie consent (every place) + client's own Google Tag Manager + GA4/Meta ecommerce events.
 *
 * Consent - EVERY place (initConsent, from App.js): nothing tracks before the visitor's choice in
 * the cookie bubble - TabL's own pixel / Google tag (index.html waits), the place's own GA4 /
 * Meta pixel / Google Ads (whenConsent), its custom code (google_analytics_code, made inert by the
 * server) and the server-side CAPI Purchase (metaCapiService, marketing consent only).
 *
 * GTM + ecommerce - ONLY for a place with the `client_gtm_id` setting (first client: Protein
 * House, GTM-NQCH9FCS); the ecommerce exports are no-ops until initClientGtm() has run.
 *
 * Contract with the client's agency (developer-handoff.html):
 *  - Consent Mode v2: analytics_storage / ad_storage / ad_user_data / ad_personalization are
 *    `denied` before GTM loads; the cookie bar's choice is applied with gtag('consent','update')
 *    and only THEN PHTracking.consentApplied(...) is called.
 *  - Events go through their adapter (phTrackingAdapter.js -> window.PHTracking); when a place
 *    has GTM but no adapter matches (another client in the future), the standard GA4 dataLayer
 *    push is used instead.
 *  - purchase: transaction_id / event_id come from the backend (metaCapiService.getTrackingIds),
 *    the same ids the server-side Conversions API uses -> Meta deduplicates browser + server.
 *
 * This module deliberately imports nothing from the store/Utils (store.js imports the
 * middleware that uses it - no import cycles).
 */
import Cookies from 'universal-cookie';
import history from '../utils/history';
import installPhTracking from './phTrackingAdapter';

const cookies = new Cookies();

export const CONSENT_COOKIE_PREFIX = 'tabl_cc_';
const SENT_PURCHASES_KEY = 'tabl_ecom_purchases_sent';
const PENDING_PURCHASE_PREFIX = 'tabl_ecom_pending_';

let initialized = false;        // the client's own GTM container is running (client_gtm_id)
let consentInitialized = false; // initConsent() ran for the opened place (every place)
const consentWaiters = [];      // { kind: 'analytics'|'marketing', fn } - see whenConsent()
let currentPlaceId = null;
let consent = { analytics: false, marketing: false };
let lastPath = null;
let checkoutItems = [];
const listeners = [];

const log = (...args) => { try { console.log('[Client GTM]', ...args); } catch (e) { } };

const gtag = function () {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(arguments);
};

const newEventId = () => {
    try {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    } catch (e) { }
    return 'ev-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
};

// ---------------------------------------------------------------- consent cookie

/** Raw cookie value for a place: 'a1.m1' / 'a1.m0' / 'a0.m0' (GTM places) or 'ack' (others). */
export const getStoredConsentRaw = (placeId) => {
    if (!placeId) return null;
    try {
        return cookies.get(CONSENT_COOKIE_PREFIX + placeId) || null;
    } catch (e) {
        return null;
    }
};

const parseConsent = (raw) => ({
    analytics: !!raw && String(raw).indexOf('a1') !== -1,
    marketing: !!raw && String(raw).indexOf('m1') !== -1,
});

const writeConsentCookie = (placeId, raw) => {
    if (!placeId) return;
    const expires = new Date();
    expires.setTime(expires.getTime() + 365 * 24 * 60 * 60 * 1000);
    const config = { path: '/', expires: expires, sameSite: 'lax' };
    if (window.location.protocol === 'https:') config.secure = true;
    try {
        cookies.set(CONSENT_COOKIE_PREFIX + placeId, raw, config);
    } catch (e) { }
};

export const isGtmMode = () => initialized;
export const getConsent = () => ({ ...consent });

/** The cookie bar subscribes here to re-render when GTM mode turns on. */
export const onTrackingChange = (fn) => {
    listeners.push(fn);
    return () => {
        const i = listeners.indexOf(fn);
        if (i !== -1) listeners.splice(i, 1);
    };
};
const notify = () => listeners.slice().forEach(fn => { try { fn(); } catch (e) { } });

// TabL's own trackers from index.html. On a consent-gated page (every menu page - meta
// tabl-consent-gated, see index.html) they did NOT start on load:
//  - Meta: fbq('consent','revoke') blocks every pixel on the page (ours, ReactPixel calls and
//    the pixel inside the client's GTM) until marketing consent.
//  - Google: UA-149580510-1 (+ its linked GA4 G-9VEMGN0Y1N) is configured only with analytics consent.
const TABL_PIXEL_ID = '2912758035505954';
const TABL_GOOGLE_TAG_ID = 'UA-149580510-1';
let tablPixelStarted = false;
let tablGoogleStarted = false;

const isTablTrackingHost = () => {
    const h = window.location.hostname;
    return h !== 'localhost' && h !== 'test.tabl.bg' && h !== 'gabrovo-pizza.tabl.bg';
};

const applyConsentToTablTrackers = (state) => {
    if (!window.__tablConsentGated) return;
    try {
        if (typeof window.fbq === 'function') {
            if (state.marketing) {
                window.fbq('consent', 'grant');
                if (!tablPixelStarted && isTablTrackingHost()) {
                    tablPixelStarted = true;
                    window.fbq('init', TABL_PIXEL_ID);
                    window.fbq('trackSingle', TABL_PIXEL_ID, 'PageView');
                }
            } else {
                window.fbq('consent', 'revoke');
            }
        }
        if (state.analytics && !tablGoogleStarted && isTablTrackingHost()) {
            tablGoogleStarted = true;
            // index.html did not load the library on a gated page.
            const s = document.createElement('script');
            s.async = true;
            s.src = 'https://www.googletagmanager.com/gtag/js?id=' + TABL_GOOGLE_TAG_ID;
            document.head.appendChild(s);
            gtag('js', new Date());
            gtag('config', TABL_GOOGLE_TAG_ID);
        }
    } catch (e) { }
};

// The place's own custom code (setting google_analytics_code) arrives inert from the server
// (<script type="text/plain" data-tabl-consent="analytics">, menuRoutes.gateConsentScripts).
const activateConsentScripts = (kind) => {
    try {
        const nodes = document.querySelectorAll('script[data-tabl-consent="' + kind + '"]');
        Array.prototype.forEach.call(nodes, old => {
            const s = document.createElement('script');
            Array.prototype.forEach.call(old.attributes, a => {
                if (a.name !== 'type' && a.name !== 'data-tabl-consent') s.setAttribute(a.name, a.value);
            });
            if (!old.src) s.text = old.text || old.textContent || '';
            old.parentNode.replaceChild(s, old);
        });
    } catch (e) { }
};

const runConsentWaiters = (state) => {
    for (let i = consentWaiters.length - 1; i >= 0; i--) {
        const w = consentWaiters[i];
        if (state[w.kind]) {
            consentWaiters.splice(i, 1);
            try { w.fn(); } catch (e) { }
        }
    }
};

/**
 * Runs fn once, as soon as the visitor has given that kind of consent (now or later).
 * Used for the place's own GA4 / Meta pixel / Google Ads (App.js) on places without GTM.
 */
export const whenConsent = (kind, fn) => {
    if (consent[kind]) {
        try { fn(); } catch (e) { }
        return;
    }
    consentWaiters.push({ kind, fn });
};

/** Google tag library for one id (G-/AW-), loaded once - nothing is loaded before consent. */
const loadedGtagLibs = {};
export const loadGtagLibrary = (id) => {
    const clean = String(id || '').replace(/\s+/g, '');
    if (!clean || loadedGtagLibs[clean]) return;
    loadedGtagLibs[clean] = true;
    try {
        const s = document.createElement('script');
        s.async = true;
        s.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(clean);
        document.head.appendChild(s);
    } catch (e) { }
};

const applyConsentToGtm = (state) => {
    // Meta consent first, so the client's pixel tags fired by consentApplied() below are not blocked.
    applyConsentToTablTrackers(state);
    if (state.analytics) activateConsentScripts('analytics');
    gtag('consent', 'update', {
        analytics_storage: state.analytics ? 'granted' : 'denied',
        ad_storage: state.marketing ? 'granted' : 'denied',
        ad_user_data: state.marketing ? 'granted' : 'denied',
        ad_personalization: state.marketing ? 'granted' : 'denied',
    });
    // Only after GTM has the new consent state (same dataLayer queue, so the order holds).
    if (window.PHTracking && typeof window.PHTracking.consentApplied === 'function') {
        window.PHTracking.consentApplied({ analytics: !!state.analytics, marketing: !!state.marketing });
    }
    runConsentWaiters(state);
};

/**
 * Every place (App.js, once the opened place is known): Consent Mode default = denied, then the
 * stored choice of this visitor (cookie tabl_cc_<placeId>) - TabL's own tags, the place's own
 * trackers and its custom code start only with the matching consent. Old 'ack' cookies (the
 * earlier "OK" bar) are not a choice -> the bubble asks again.
 */
export const initConsent = (placeId) => {
    if (consentInitialized || !placeId) return;
    consentInitialized = true;
    currentPlaceId = placeId;
    window.dataLayer = window.dataLayer || [];

    gtag('consent', 'default', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        wait_for_update: 500,
    });

    const raw = getStoredConsentRaw(placeId);
    if (raw && raw !== 'ack') {
        consent = parseConsent(raw);
        applyConsentToGtm(consent);
    }
    notify();
};

export const isConsentReady = () => consentInitialized;

/**
 * Called by the cookie bubble (every place): stores the choice and starts what it allows.
 * A withdrawal reloads the page - already loaded vendor scripts keep listening otherwise.
 */
export const saveConsent = (placeId, state) => {
    if (!consentInitialized) initConsent(placeId);

    const next = { analytics: !!state.analytics, marketing: !!state.marketing };
    const withdrawn = (consent.analytics && !next.analytics) || (consent.marketing && !next.marketing);

    writeConsentCookie(placeId, (next.analytics ? 'a1' : 'a0') + '.' + (next.marketing ? 'm1' : 'm0'));
    consent = next;

    if (withdrawn) {
        applyConsentToGtm(next);
        log('consent withdrawn -> reload');
        setTimeout(() => window.location.reload(), 150);
        return;
    }

    applyConsentToGtm(next);
    notify();
};

// ---------------------------------------------------------------- GTM bootstrap

/**
 * Loads the client's GTM container once (App.js, after the place settings arrive).
 * Order: consent default (denied) -> GTM loader -> adapter -> stored choice (if any).
 */
export const initClientGtm = (gtmId, placeId) => {
    if (initialized) return;
    const id = String(gtmId || '').replace(/\s+/g, '');
    if (!/^GTM-[A-Z0-9]+$/i.test(id)) {
        log('invalid client_gtm_id', gtmId);
        return;
    }

    // Consent default (denied) + the stored choice go to the dataLayer BEFORE the container loads.
    initConsent(placeId);
    initialized = true;
    currentPlaceId = placeId;

    // Standard GTM snippet (developer-handoff.html, section 3). No noscript iframe on purpose.
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtm.js?id=' + encodeURIComponent(id);
    const first = document.getElementsByTagName('script')[0];
    if (first && first.parentNode) {
        first.parentNode.insertBefore(script, first);
    } else {
        document.head.appendChild(script);
    }

    try {
        installPhTracking();
    } catch (e) {
        log('adapter install failed', e);
    }

    // The adapter exists only now - hand it the stored choice (no-op for the trackers already started).
    if (consent.analytics || consent.marketing) applyConsentToGtm(consent);

    // SPA page views: only real path changes, never the initial render.
    lastPath = window.location.pathname;
    history.listen(location => {
        const path = (location && location.pathname) || window.location.pathname;
        if (path === lastPath) return;
        lastPath = path;
        // Let the route render (title/url updated) before reporting the page view.
        setTimeout(() => {
            if (window.PHTracking && typeof window.PHTracking.pageChanged === 'function') {
                window.PHTracking.pageChanged();
            } else if (consent.analytics) {
                window.dataLayer.push({ event: 'page_view_virtual', page_location: window.location.href });
            }
        }, 0);
    });

    log('GTM loaded', id, '| consent', consent);
    notify();
};

// ---------------------------------------------------------------- ecommerce

const toNumber = (v) => {
    const n = parseFloat(String(v === undefined || v === null ? '' : v).replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
};
const round2 = (n) => Math.round(n * 100) / 100;

/** One cart line (products.to_order entry) -> GA4 item. item_id = TabL product id. */
export const cartLineToItem = (line, quantity = null) => {
    if (!line || !line.product_id) return null;
    const qty = quantity !== null ? quantity : (parseInt(line.quantity) || 1);
    const item = {
        item_id: String(line.product_id),
        item_name: line.name || '',
        price: round2(toNumber(line.price)),
        quantity: qty,
    };
    if (line.size) item.item_variant = String(line.size);
    return item;
};

/** Every line of products.to_order ({ pvId: [lines] } or its sortBy() array form). */
export const cartLines = (toOrder) => {
    const out = [];
    if (!toOrder) return out;
    Object.values(toOrder).forEach(group => {
        if (Array.isArray(group)) {
            group.forEach(line => { if (line && line.product_id) out.push(line); });
        } else if (group && group.product_id) {
            out.push(group);
        }
    });
    return out;
};

export const cartToItems = (toOrder) => cartLines(toOrder).map(l => cartLineToItem(l)).filter(Boolean);

export const itemsValue = (items) => round2((items || []).reduce((sum, i) => sum + i.price * i.quantity, 0));

/**
 * Sends one GA4 ecommerce event (+ its Meta twin through the adapter).
 * No-op outside GTM mode or without any consent; never throws.
 */
export const trackEcommerce = (name, ecommerce, eventId = null) => {
    if (!initialized) return false;
    if (!consent.analytics && !consent.marketing) return false;
    if (!ecommerce || !Array.isArray(ecommerce.items) || ecommerce.items.length === 0) return false;

    const id = eventId || newEventId();
    try {
        if (window.PHTracking && typeof window.PHTracking.ecommerce === 'function') {
            const sent = window.PHTracking.ecommerce(name, ecommerce, id);
            log(name, sent ? 'sent' : 'skipped', ecommerce);
            return sent;
        }
        // Generic GTM place (no agency adapter): plain GA4 dataLayer contract.
        if (consent.analytics) {
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({ event: name, event_id: id, ecommerce: ecommerce });
            log(name, 'pushed', ecommerce);
            return true;
        }
    } catch (e) {
        log(name, 'rejected:', e && e.message, ecommerce);
    }
    return false;
};

// Items of the cart at the moment "Поръчай" was pressed (the cart is cleared afterwards).
export const setCheckoutItems = (items) => { checkoutItems = Array.isArray(items) ? items : []; };
export const getCheckoutItems = () => checkoutItems;

/** Card orders: the Purchase is reported on the payment page after a full reload -> keep the items. */
export const savePendingPurchase = (orderKey, data) => {
    if (!orderKey) return;
    try {
        window.localStorage.setItem(PENDING_PURCHASE_PREFIX + orderKey, JSON.stringify({ ...data, t: Date.now() }));
    } catch (e) { }
};

const takePendingPurchase = (orderKey) => {
    if (!orderKey) return null;
    try {
        const raw = window.localStorage.getItem(PENDING_PURCHASE_PREFIX + orderKey);
        if (!raw) return null;
        window.localStorage.removeItem(PENDING_PURCHASE_PREFIX + orderKey);
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
};

const wasPurchaseSent = (eventId) => {
    try {
        const list = JSON.parse(window.localStorage.getItem(SENT_PURCHASES_KEY) || '[]');
        return Array.isArray(list) && list.indexOf(eventId) !== -1;
    } catch (e) {
        return false;
    }
};

const markPurchaseSent = (eventId) => {
    try {
        let list = JSON.parse(window.localStorage.getItem(SENT_PURCHASES_KEY) || '[]');
        if (!Array.isArray(list)) list = [];
        list.push(eventId);
        window.localStorage.setItem(SENT_PURCHASES_KEY, JSON.stringify(list.slice(-50)));
    } catch (e) { }
};

/**
 * purchase - only with ids from the backend (never generated here), once per order even across
 * refresh / back / revisit (localStorage list of sent event ids).
 *
 * @param {object} p { tracking: {transaction_id, event_id}, value, currency, items, orderKey }
 */
export const trackPurchase = (p, tries = 0) => {
    // The payment-result page is a fresh load: GTM is initialised when the settings arrive.
    if (!initialized) {
        if (tries < 20) setTimeout(() => trackPurchase(p, tries + 1), 500);
        return;
    }

    const pending = takePendingPurchase(p.orderKey);
    const tracking = (p.tracking && p.tracking.event_id) ? p.tracking : (pending && pending.tracking);
    if (!tracking || !tracking.event_id || !tracking.transaction_id) {
        log('purchase skipped - no backend tracking ids');
        return;
    }
    if (wasPurchaseSent(tracking.event_id)) {
        log('purchase already sent', tracking.event_id);
        return;
    }

    const items = (p.items && p.items.length ? p.items : (pending && pending.items)) || [];
    const value = round2(toNumber(p.value || (pending && pending.value)));

    const sent = trackEcommerce('purchase', {
        transaction_id: String(tracking.transaction_id),
        currency: p.currency || (pending && pending.currency) || 'EUR',
        value: value,
        items: items,
    }, String(tracking.event_id));

    // No consent -> not sent, and not replayed if consent comes later (agency rule).
    markPurchaseSent(tracking.event_id);
    log('purchase', sent ? 'sent' : 'not sent (consent)', tracking.event_id);
};

export const getCurrentGtmPlaceId = () => currentPlaceId;
